import { Form, InputNumber, Select } from "antd";
import { CompanyParticipationType } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { useProjectParticipants } from "hooks/project-participants";
import { useContext, useEffect, useState } from "react";
import { restrictInputToNumbers } from "utils/inpututils";
import { updatePropertyAtPath } from "utils/utils";
import _ from "lodash";

function Participants(props: {
  actions?: any;
  featureInstanceData?: any;
  disabled: boolean;
}) {
  const { actions, featureInstanceData, disabled } = props;
  const [form] = Form.useForm();
  const { projectParticipants } = useProjectParticipants();
  const subContractors = projectParticipants?.materialTradePartners;
  const {
    columnHeaders: { bidPackageHeaderMap }
  } = useContext(ProjectContext);

  const [localFeatureInstanceData, setLocalFeatureInstanceData] =
    useState<any>();

  useEffect(() => {
    if (!featureInstanceData) return;
    setLocalFeatureInstanceData(structuredClone(featureInstanceData));
  }, [featureInstanceData]);

  const biddingSubContractor =
    localFeatureInstanceData?.feature_company_participants?.filter(
      (company: any) =>
        company?.company_participation_type_id ===
        CompanyParticipationType.BIDDING_SUB_CONTRACTOR
    );

  const awardedSubContractor =
    localFeatureInstanceData?.feature_company_participants?.find(
      (company: any) =>
        company?.company_participation_type_id ===
        CompanyParticipationType.AWARDED_SUB_CONTRACTOR
    );

  useEffect(() => {
    if (!localFeatureInstanceData) return;
    form.setFieldsValue({
      no_of_bidders:
        localFeatureInstanceData?.feature_bid_package?.no_of_bidders,
      estimated_value:
        localFeatureInstanceData?.feature_bid_package?.estimated_value,
      awarded_value:
        localFeatureInstanceData?.feature_bid_package?.awarded_value,
      bidding_subcontractors: biddingSubContractor?.map(
        (c: any) => c?.vendor_id
      ),
      awarded_subcontractor: awardedSubContractor?.vendor_id
    });
  }, [
    awardedSubContractor?.vendor_id,
    biddingSubContractor,
    form,
    localFeatureInstanceData
  ]);

  const updateParticipantInputFields = async (key: string, value: any) => {
    const param: any = {};
    param[key] = value;
    const response = await actions.updateBidPackageFields(param);
    if (!response.success) {
      form.setFieldsValue({ key: value });
    }
  };

  const updateLocallyCompanyParticipants = (vendorId: string) => {
    const selectedCompany = subContractors?.find(
      (cmpny: any) => cmpny?.vendor_id === vendorId
    );

    if (selectedCompany) {
      const company = {
        vendor_id: vendorId,
        instance_id: localFeatureInstanceData?.id,
        company_participation_type_id:
          CompanyParticipationType.AWARDED_SUB_CONTRACTOR,
        vendor: {
          name: selectedCompany?.name
        }
      };

      if (localFeatureInstanceData?.feature_company_participants?.length) {
        localFeatureInstanceData?.feature_company_participants?.splice(
          0,
          1,
          company
        );
      } else {
        localFeatureInstanceData?.feature_company_participants?.push(company);
      }

      setLocalFeatureInstanceData(
        updatePropertyAtPath(
          localFeatureInstanceData,
          "feature_company_participants",
          localFeatureInstanceData?.feature_company_participants
        )
      );
    }
  };

  return (
    <Form layout="vertical" form={form}>
      <Form.Item
        label={bidPackageHeaderMap?.no_of_bidders}
        name="no_of_bidders"
      >
        <InputNumber
          disabled={disabled}
          className="w-full"
          type="number"
          min={0}
          onKeyDown={restrictInputToNumbers}
          onBlur={(event: any) => {
            const oldValue =
              localFeatureInstanceData?.feature_bid_package?.no_of_bidders;
            const newValue = Number(event?.target?.value?.trim());
            if (oldValue === newValue) return;
            setLocalFeatureInstanceData(
              updatePropertyAtPath(
                localFeatureInstanceData,
                "feature_bid_package",

                {
                  ...localFeatureInstanceData?.feature_bid_package,
                  no_of_bidders: newValue
                }
              )
            );
            updateParticipantInputFields("no_of_bidders", newValue);
          }}
        />
      </Form.Item>
      <Form.Item
        label={bidPackageHeaderMap?.bidding_subcontractors}
        name={["bidding_subcontractors"]}
      >
        <Select
          disabled={disabled}
          mode="multiple"
          options={
            subContractors
              ? subContractors.map((company: any) => ({
                  id: company.vendor_id,
                  label: company.subscription_vendor.name,
                  value: company.vendor_id
                }))
              : []
          }
          onBlur={() => {
            const ids = form.getFieldValue(["bidding_subcontractors"]);
            if (
              _.isEqual(
                biddingSubContractor?.map((c: any) => c?.vendor_id),
                ids
              )
            )
              return;

            actions.updateFeatureCompanyParticipant({
              vendorIds: ids,
              companyParticipantTypeId:
                CompanyParticipationType.BIDDING_SUB_CONTRACTOR
            });
          }}
          removeIcon={null}
          showArrow
        />
      </Form.Item>
      <div className="flex space-x-2 w-full">
        <Form.Item
          className="grow"
          label={bidPackageHeaderMap?.estimated_value}
          name="estimated_value"
        >
          <InputNumber
            disabled={disabled}
            className="w-full"
            addonBefore="$"
            min={0}
            onKeyDown={restrictInputToNumbers}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            onBlur={(event: any) => {
              const oldValue =
                localFeatureInstanceData?.feature_bid_package?.estimated_value;
              const newValue = Number(event?.target?.value?.replace(/,/g, ""));

              if (oldValue === newValue) return;
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_bid_package",

                  {
                    ...localFeatureInstanceData?.feature_bid_package,
                    estimated_value: newValue
                  }
                )
              );
              updateParticipantInputFields("estimated_value", newValue);
            }}
          />
        </Form.Item>
        <Form.Item
          className="grow"
          label={bidPackageHeaderMap?.awarded_value}
          name="awarded_value"
        >
          <InputNumber
            disabled={disabled}
            className="w-full"
            addonBefore="$"
            min={0}
            onKeyDown={restrictInputToNumbers}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            onBlur={(event: any) => {
              const oldValue =
                localFeatureInstanceData?.feature_bid_package?.awarded_value;
              const newValue = Number(event?.target?.value?.replace(/,/g, ""));

              if (oldValue === newValue) return;
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_bid_package",

                  {
                    ...localFeatureInstanceData?.feature_bid_package,
                    awarded_value: newValue
                  }
                )
              );
              updateParticipantInputFields("awarded_value", newValue);
            }}
          />
        </Form.Item>
      </div>
      <Form.Item
        label={bidPackageHeaderMap?.awarded_subcontractor}
        name="awarded_subcontractor"
      >
        <Select
          disabled={disabled}
          options={
            subContractors
              ? subContractors.map((company: any) => ({
                  label: company.subscription_vendor.name,
                  value: company.vendor_id
                }))
              : []
          }
          onChange={(vendorId) => {
            updateLocallyCompanyParticipants(vendorId);
            actions.updateFeatureCompanyParticipant({
              vendorIds: [vendorId],
              companyParticipantTypeId:
                CompanyParticipationType.AWARDED_SUB_CONTRACTOR
            });
          }}
          allowClear
        />
      </Form.Item>
    </Form>
  );
}

export default Participants;
